import {
  DEFAULT_ORDER_DURATION_SECONDS,
  CUSTOM_EXPIRY_MIN_SECONDS,
  CUSTOM_EXPIRY_MAX_SECONDS,
  HOUSE_EDGE_RATIO,
  ROUND_VARIANTS,
} from "../constants/upDownConstants";

export function formatTime(seconds) {
  const safe = Math.max(0, Number(seconds) || 0);
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

export function formatPairLabelFromSymbol(symbol) {
  if (typeof symbol !== "string" || symbol.length === 0) {
    return "";
  }

  const normalizedSymbol = symbol.replaceAll("_", "").toUpperCase();
  const knownQuoteAsset = [
    "USDT",
    "USDC",
    "BUSD",
    "USD",
    "BTC",
    "ETH",
    "EUR",
    "JPY",
    "GBP",
  ].find(
    (quoteAsset) =>
      normalizedSymbol.endsWith(quoteAsset) &&
      normalizedSymbol.length > quoteAsset.length,
  );

  if (!knownQuoteAsset) {
    return normalizedSymbol;
  }

  return `${normalizedSymbol.slice(0, -knownQuoteAsset.length)}/${knownQuoteAsset}`;
}

export function normalizePairIdentity(value) {
  return String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

export function formatMultiplier(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 1) {
    return "-";
  }
  if (Number.isInteger(numericValue)) {
    return `${numericValue.toFixed(0)}x`;
  }
  return `${numericValue.toFixed(2)}x`;
}

export function toBackendPairSymbol(symbol, label) {
  if (typeof label === "string" && label.includes("/")) {
    return label.replace("/", "_").toUpperCase();
  }

  if (typeof symbol !== "string" || symbol.length === 0) {
    return "BTC_USDT";
  }

  const upperSymbol = symbol.toUpperCase();
  const knownQuotes = ["USDT", "USDC", "BUSD", "FDUSD", "BTC", "ETH"];
  const quote = knownQuotes.find((item) => upperSymbol.endsWith(item));
  if (!quote) {
    return upperSymbol;
  }

  const base = upperSymbol.slice(0, upperSymbol.length - quote.length);
  return base ? `${base}_${quote}` : upperSymbol;
}

export const getBaseCoin = (pair) => {
  if (!pair) return "";
  const symbol = pair.toUpperCase();
  const knownQuotes = ["USDT", "USD", "INR", "EUR", "BTC", "ETH"];
  const quote = knownQuotes.find((q) => symbol.endsWith(q));
  if (!quote) return symbol;
  return symbol.replace(quote, "");
};

export function extractCoinSymbol(pairSymbol) {
  const normalized = String(pairSymbol || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

  if (!normalized) {
    return "BTC";
  }

  const knownQuotes = [
    "USDT",
    "USDC",
    "BUSD",
    "FDUSD",
    "USD",
    "BTC",
    "ETH",
    "BNB",
    "EUR",
    "TRY",
  ];
  const quote = knownQuotes.find(
    (item) => normalized.endsWith(item) && normalized.length > item.length,
  );
  if (quote) {
    return normalized.slice(0, normalized.length - quote.length) || normalized;
  }

  return normalized.slice(0, 4);
}

export function createBidRequestId(pair) {
  const pairPart =
    String(pair || "PAIR")
      .replace(/[^A-Z0-9_]/gi, "")
      .toUpperCase() || "PAIR";
  const randomPart = Math.random().toString(36).slice(2, 10);
  return `${pairPart}-${Date.now()}-${randomPart}`;
}

export function parseStake(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return Math.max(0, parsed);
}

export function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

export function clampCustomExpirySeconds(
  value,
  fallback = DEFAULT_ORDER_DURATION_SECONDS,
) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return clamp(
    Math.round(parsed),
    CUSTOM_EXPIRY_MIN_SECONDS,
    CUSTOM_EXPIRY_MAX_SECONDS,
  );
}

export function roundToCents(value) {
  const round3 = Math.round(value * 1000) / 1000;
  const round2 = Math.round(value * 100) / 100;
  if (round3 !== round2 && Math.abs(value) < 1) {
    return round3;
  }
  return round2;
}

export function roundToOneDecimal(value) {
  return Math.round(value * 10) / 10;
}

export function clampStake(amount, minimumStake, maximumStake) {
  const normalized = Number.isFinite(amount) ? amount : minimumStake;
  return clamp(roundToCents(normalized), minimumStake, maximumStake);
}

export function resolvePayoutPercent(totalPool, sidePool) {
  if (!Number.isFinite(totalPool) || !Number.isFinite(sidePool)) {
    return 100;
  }
  if (totalPool <= 0 || sidePool <= 0) {
    return 100;
  }
  const multiplier = (totalPool * (1 - HOUSE_EDGE_RATIO)) / sidePool;
  return Math.max(100, roundToOneDecimal(multiplier * 100));
}

export function randomInt(min, max) {
  const lower = Math.ceil(min);
  const upper = Math.floor(max);
  return Math.floor(Math.random() * (upper - lower + 1)) + lower;
}

export function toAmountInput(value) {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

export function resolveVariantIdByDuration(durationSeconds) {
  const normalizedDuration = Number(durationSeconds);
  if (!Number.isFinite(normalizedDuration) || normalizedDuration <= 0) {
    return null;
  }

  return (
    ROUND_VARIANTS.find(
      (option) =>
        option.seconds === normalizedDuration ||
        option.tradeWindowSeconds === normalizedDuration,
    )?.id ?? null
  );
}

export function formatUsd(value) {
  const safe = Number.isFinite(value) ? value : 0;
  const abs = Math.abs(safe);
  const fixed3 = Number(abs.toFixed(3));
  const fixed2 = Number(abs.toFixed(2));
  const fractionDigits = abs > 0 && abs < 1 && fixed3 !== fixed2 ? 3 : 2;

  return safe.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

export function formatPrice(value) {
  const safe = Number.isFinite(value) ? value : 0;
  return safe.toLocaleString("en-US", {
    minimumFractionDigits: safe >= 1000 ? 4 : 6,
    maximumFractionDigits: safe >= 1000 ? 4 : 6,
  });
}

export function formatPercent(value) {
  return `${Math.round(value)}%`;
}

export function easeOutCubic(progress) {
  const clamped = clamp(progress, 0, 1);
  return 1 - Math.pow(1 - clamped, 3);
}

export function normalizeBackendPairType(type) {
  if (type === "forex") {
    return "forex";
  }
  if (type === "commodity" || type === "commodities") {
    return "commodity";
  }
  return "crypto";
}

export function toTradeDateTime(unixSeconds) {
  const parsedSeconds = Number(unixSeconds);
  if (!Number.isFinite(parsedSeconds) || parsedSeconds <= 0) {
    return undefined;
  }
  return new Date(parsedSeconds * 1000).toISOString();
}

